import { setCurrentUserData } from 'store/reducers/authorizeReducer';
import type { CurrentUserPayload } from 'store/reducers/types';
import type { AppDispatch, AppThunk } from 'store';
import { ROUTES } from 'router/constants';
import { fetchWrapper } from '../helpers/helpers';
import history from '../helpers/history';
import { MixpanelService } from '../helpers/Mixpanel';

const mixpanel = new MixpanelService();

interface AuthTokenResponse {
  token?: string;
  detail?: string;
}

export type SignInCallback = (status: 'error' | 'errorEmail') => void;

const onAfterLogin = (): void => {
  if (window.localStorage.getItem('link_to_page')) {
    const pathname = window.localStorage.getItem('link_to_page') as string;
    window.localStorage.removeItem('link_to_page');
    history.push(pathname);
  } else {
    history.push(ROUTES.PORTAL);
  }
};

const onAfterCalendarConnect =
  (dispatch: AppDispatch, uuid: string) =>
  ({ code }: GoogleCodeResponse): void => {
    fetchWrapper({
      method: 'POST',
      url: 'auth/token/',
      body: { code },
      skipTokenCheck: true,
    }).then(() => {
      fetchWrapper({
        method: 'PATCH',
        url: `users/${uuid}/profile/`,
        body: { has_calendar_access: true },
      }).then(() => {
        fetchWrapper<CurrentUserPayload>({
          url: 'users/current/',
        }).then(r => {
          if (r) dispatch(setCurrentUserData(r));
          onAfterLogin();
        });
      });
    });
  };

const onErrorCalendarConnect =
  (dispatch: AppDispatch, uuid: string) =>
  (err: GoogleErrorResponse): void => {
    if (err.error === 'access_denied') {
      fetchWrapper({
        method: 'PATCH',
        url: `users/${uuid}/profile/`,
        body: { has_calendar_access: false },
      }).then(() => {
        fetchWrapper<CurrentUserPayload>({
          url: 'users/current/',
        }).then(r => {
          if (r) dispatch(setCurrentUserData(r));
          onAfterLogin();
        });
      });
    } else {
      onAfterLogin();
    }
  };

export function signIn(code: string, callback: SignInCallback): AppThunk<Promise<AuthTokenResponse | undefined>> {
  return dispatch =>
    fetchWrapper<AuthTokenResponse>({
      method: 'POST',
      url: 'auth/token/',
      body: { code },
      skipTokenCheck: true,
    })
      .then(payload => {
        if (payload?.token) {
          window.localStorage.setItem('access_token', payload.token);
          fetchWrapper<CurrentUserPayload>({
            url: 'users/current/',
          })
            .then(resp => {
              if (!resp || !resp.roles.length) {
                window.localStorage.removeItem('access_token');
                callback('error');
                return;
              }
              dispatch(setCurrentUserData(resp));
              mixpanel.login(resp);
              if (resp.profile?.has_calendar_access === null || !resp.has_refresh_token) {
                window.google.accounts.oauth2
                  .initCodeClient({
                    client_id: process.env.REACT_APP_GA_CLIENT_ID as string,
                    scope: 'https://www.googleapis.com/auth/calendar https://www.googleapis.com/auth/calendar.events',
                    callback: onAfterCalendarConnect(dispatch, resp.uuid),
                    error_callback: onErrorCalendarConnect(dispatch, resp.uuid),
                  })
                  .requestCode();
              } else {
                onAfterLogin();
              }
            })
            .catch(() => {
              history.push(ROUTES.LOGIN);
            });
          return undefined;
        }
        return payload ?? undefined;
      })
      .catch((err: { detail?: string }) => {
        if (err.detail === 'User with provided email is not found') {
          callback('errorEmail');
        } else {
          callback('error');
        }
        return undefined;
      });
}

export function signOut(): void {
  fetchWrapper({
    method: 'POST',
    url: 'auth/logout/',
  })
    .then(() => {
      window.localStorage.removeItem('access_token');
      sessionStorage.clear();
      history.push(ROUTES.LOGIN);
      mixpanel.logout();
    })
    .catch(() => {
      window.localStorage.removeItem('access_token');
      history.push(ROUTES.LOGIN);
      mixpanel.logout();
    });
}

export function getUserData(): AppThunk<Promise<void>> {
  return dispatch =>
    fetchWrapper<CurrentUserPayload>({
      url: 'users/current/',
    })
      .then(payload => {
        if (!payload || !payload.roles.length) {
          window.localStorage.removeItem('access_token');
          history.push(ROUTES.LOGIN, { error: 'error' });
          return;
        }
        dispatch(setCurrentUserData(payload));
        mixpanel.identify(payload);
      })
      .catch(() => {
        history.push(ROUTES.LOGIN);
      });
}
